import { Entity, UniqueEntityID } from '@domain/core'
import { AircraftType, EngineType, FuelType } from '@domain/protocols/AircraftType'

import { AltitudeUnit, FuelConsumptionUnit, RateUnit, SpeedUnit } from '@/domain/protocols/Unit'

import { unitConverters } from '@/utils/unit'

export interface AircraftModelProps {
  aircraftType?: AircraftType
  designatorIcao?: string
  engineModel?: string
  engineType?: EngineType
  fuelConsumptionUnit: FuelConsumptionUnit
  fuelConsumptionAc: number
  fuelConsumptionCruise: number
  fuelConsumptionDc: number
  fuelType: FuelType
  model: string
  operationalCeiling: number
  operationalCeilingUnit: AltitudeUnit
  rateUnit: RateUnit
  rateAc: number
  rateDc: number
  speedUnit: SpeedUnit
  speedAc: number
  speedCruise: number
  speedDc: number

  createdAt: Date
  updatedAt: Date
}

export interface AircraftModelPojo extends AircraftModelProps {
  id: string
}

export class AircraftModel extends Entity<AircraftModelProps, AircraftModelPojo> {
  private constructor(props: AircraftModelProps, id?: UniqueEntityID) {
    super(props, id)
  }

  public static create(props: AircraftModelProps, id?: UniqueEntityID): AircraftModel {
    return new AircraftModel(props, id)
  }

  public static convertUnitsAndCreate(props: AircraftModelProps, id?: UniqueEntityID): AircraftModel {
    const aircraft = new AircraftModel(props, id)

    aircraft.speedAc = unitConverters.speed(props.speedAc, props.speedUnit, true)
    aircraft.rateAc = unitConverters.rate(props.rateAc, props.rateUnit, true)
    aircraft.fuelConsumptionAc = unitConverters.fuel(
      props.fuelConsumptionAc,
      props.fuelType,
      props.fuelConsumptionUnit,
      true
    )
    aircraft.speedCruise = unitConverters.speed(props.speedCruise, props.speedUnit, true)
    aircraft.fuelConsumptionCruise = unitConverters.fuel(
      props.fuelConsumptionCruise,
      props.fuelType,
      props.fuelConsumptionUnit,
      true
    )
    aircraft.operationalCeiling = unitConverters.altitude(props.operationalCeiling, props.operationalCeilingUnit, true)
    aircraft.speedDc = unitConverters.speed(props.speedDc, props.speedUnit, true)
    aircraft.rateDc = unitConverters.rate(props.rateDc, props.rateUnit, true)
    aircraft.fuelConsumptionDc = unitConverters.fuel(
      props.fuelConsumptionDc,
      props.fuelType,
      props.fuelConsumptionUnit,
      true
    )

    return aircraft
  }

  public get createdAt(): Date {
    return this.props.createdAt
  }

  public set createdAt(createdAt: Date) {
    this.props.createdAt = createdAt
  }

  public get updatedAt(): Date {
    return this.props.updatedAt
  }

  public set updatedAt(updatedAt: Date) {
    this.props.updatedAt = updatedAt
  }

  public get model(): string {
    return this.props.model
  }

  public set model(model: string) {
    this.props.model = model
  }

  public get aircraftType(): AircraftType {
    return this.props.aircraftType
  }

  public set aircraftType(aircraftType: AircraftType) {
    this.props.aircraftType = aircraftType
  }

  public get engineType(): EngineType {
    return this.props.engineType
  }

  public set engineType(engineType: EngineType) {
    this.props.engineType = engineType
  }

  public get engineModel() {
    return this.props.engineModel
  }

  public set engineModel(engineModel: string) {
    this.props.engineModel = engineModel
  }

  public get designatorIcao(): string {
    return this.props.designatorIcao
  }

  public set designatorIcao(designatorIcao: string) {
    this.props.designatorIcao = designatorIcao
  }

  public get fuelType(): FuelType {
    return this.props.fuelType
  }

  public set fuelType(fuelType: FuelType) {
    this.props.fuelType = fuelType
  }

  public get speedUnit(): SpeedUnit {
    return this.props.speedUnit
  }

  public set speedUnit(speedUnit: SpeedUnit) {
    this.props.speedUnit = speedUnit
  }

  public get speedAc(): number {
    return this.props.speedAc
  }

  public set speedAc(speedAc: number) {
    this.props.speedAc = speedAc
  }

  public get speedCruise(): number {
    return this.props.speedCruise
  }

  public set speedCruise(speedCruise: number) {
    this.props.speedCruise = speedCruise
  }

  public get speedDc(): number {
    return this.props.speedDc
  }

  public set speedDc(speedDc: number) {
    this.props.speedDc = speedDc
  }

  public get rateUnit(): RateUnit {
    return this.props.rateUnit
  }

  public set rateUnit(rateUnit: RateUnit) {
    this.props.rateUnit = rateUnit
  }

  public get rateAc(): number {
    return this.props.rateAc
  }

  public set rateAc(rateAc: number) {
    this.props.rateAc = rateAc
  }

  public get fuelConsumptionUnit(): FuelConsumptionUnit {
    return this.props.fuelConsumptionUnit
  }

  public set fuelConsumptionUnit(fuelConsumptionUnit: FuelConsumptionUnit) {
    this.props.fuelConsumptionUnit = fuelConsumptionUnit
  }

  public get fuelConsumptionAc(): number {
    return this.props.fuelConsumptionAc
  }

  public set fuelConsumptionAc(fuelConsumptionAc: number) {
    this.props.fuelConsumptionAc = fuelConsumptionAc
  }

  public get fuelConsumptionCruise(): number {
    return this.props.fuelConsumptionCruise
  }

  public set fuelConsumptionCruise(fuelConsumptionCruise: number) {
    this.props.fuelConsumptionCruise = fuelConsumptionCruise
  }

  public get fuelConsumptionDc(): number {
    return this.props.fuelConsumptionDc
  }

  public set fuelConsumptionDc(fuelConsumptionDc: number) {
    this.props.fuelConsumptionDc = fuelConsumptionDc
  }

  public get operationalCeiling(): number {
    return this.props.operationalCeiling
  }

  public set operationalCeiling(operationalCeiling: number) {
    this.props.operationalCeiling = operationalCeiling
  }

  public get operationalCeilingUnit(): AltitudeUnit {
    return this.props.operationalCeilingUnit
  }

  public set operationalCeilingUnit(operationalCeilingUnit: AltitudeUnit) {
    this.props.operationalCeilingUnit = operationalCeilingUnit
  }

  public get rateDc(): number {
    return this.props.rateDc
  }

  public set rateDc(rateDc: number) {
    this.props.rateDc = rateDc
  }
}
